'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/ui/shadcn/drawer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import { LegalDocumentPage } from '@/features/support/ui/LegalDocumentPage';
import type { PaywallPayload } from '@/shared/lib/api';
import {
  AnalyticsEvent,
  PackTrigger,
  PaywallSurface,
  trackEvent,
} from '@/shared/lib/analytics';
import { formatPackPrice } from '@/shared/lib/format-catalog-price';
import {
  findPackProduct,
  useStoreCatalog,
} from '@/shared/api/hooks/use-store-catalog';
import { useProject } from '@/features/project/hooks/use-projects';
import { PaywallBullets } from '@/features/payment/ui/PaywallBullets';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { ArrowLeft, BadgeCheck, Lock } from 'lucide-react';
import { PackBuyCta } from './PackBuyCta';

/**
 * Facade Pack paywall body — rendered inside the shared `LimitPaywallDialog`
 * whenever the server payload's primary action is `purchase_pack`
 * (PACK_REQUIRED, FREE_DESIGNS_EXHAUSTED). The PACK_*_EXHAUSTED cap codes are
 * info-only now (null primary action) and never reach this body — they fall
 * back to the generic limit layout.
 *
 * All offer copy (title / body / outcome bullets / action labels) comes from
 * the server payload verbatim — the client adds only chrome: the building
 * binding line ("For {building}"), the reference price from GET /v1/store,
 * and the direct-to-app block (web has no purchases; the mobile apps own the
 * buy flow).
 *
 * Pro Plan (Pro Plan) is a global unlimited entitlement — it unlocks every
 * building with no per-building activation, so holders never hit this paywall.
 */
export function PackPaywallContent({
  payload,
  projectId,
  onDismiss,
  onCheckoutOpen,
  onCheckoutClose,
  onBack,
}: {
  payload: PaywallPayload;
  /** Building the limit fired on (error meta) or the current route's project. */
  projectId: string | undefined;
  onDismiss: () => void;
  /** Paddle overlay opened — host relaxes its modal lock so it's clickable. */
  onCheckoutOpen?: () => void;
  /** Paddle overlay dismissed (purchase OR cancel) — host re-locks. */
  onCheckoutClose?: () => void;
  /**
   * Back arrow shown left of the title, swapping this body back to the host
   * dialog's parent state instead of closing. Passed only when this body was
   * reached by swapping a PARENT sheet in place (the bulletless pack-offer
   * alert) — a rich offer is the entry point itself and has nothing to go
   * back to, so it's omitted there.
   */
  onBack?: () => void;
}) {
  const t = useTranslations('packs');
  const tCommon = useTranslations('common');
  const tLegal = useTranslations('account');

  // Funnel: the pack paywall surfaced. Once per mount — the dialog remounts
  // per open, so a ref guard is enough.
  const shownLogged = useRef(false);
  useEffect(() => {
    if (shownLogged.current) return;
    shownLogged.current = true;
    trackEvent(AnalyticsEvent.PACK_PAYWALL_SHOWN, {
      surface: PaywallSurface.PACK,
      trigger: PackTrigger.PURCHASE_PACK,
      project_bound: !!projectId,
    });
  }, [projectId]);

  // Building chrome — the pack is bound to ONE building, so the offer names
  // it. Project detail is normally already cached on generation surfaces;
  // the line simply hides while unknown.
  const { data: projectResponse } = useProject(projectId ?? '', {
    enabled: !!projectId,
  });
  const buildingName = projectResponse?.data?.attributes?.name;
  const isPackActive =
    projectResponse?.data?.attributes?.pack?.is_active === true;

  // The buy CTA always names the bound building ("Get kit for "<name>"") — the
  // pack attaches to exactly this one building, so naming it removes any doubt
  // about what the purchase unlocks (parity with Apple/Android, which name the
  // building on the button too). When the name isn't cached yet the label falls
  // back to the generic buy verb inside PackBuyCta.
  const buildingVerbLabel = buildingName
    ? t('getKitForBuilding', { name: buildingName as string })
    : undefined;

  // Reference price + SKU label from the server catalog. Failure-tolerant:
  // without it the dialog still shows the full server payload — only the
  // price line drops.
  const { data: catalog } = useStoreCatalog({
    projectId,
    enabled: true,
  });
  const product = findPackProduct(catalog);
  const productAttrs = product?.attributes;

  /** Flips the body into the purchase celebration on checkout.completed —
   *  staying open as the reward moment, mirroring Apple/Android (which keep the
   *  sheet up on a checkmark celebration rather than vanishing). */
  const [didPurchase, setDidPurchase] = useState(false);

  // In-app Terms — a nested dialog OVER the paywall (keeps the buyer next to the
  // CTA), reusing the same LegalDocumentPage the Account screen renders. Parity
  // with the Pass paywall + Apple/Android sheet footers. No "Restore" on web:
  // Paddle entitlements are account-bound server-side, nothing device-local to
  // restore.
  const [showTerms, setShowTerms] = useState(false);

  // Reconcile backstop (realtime rule): the success celebration normally flips
  // on Paddle's `checkout.completed` JS event (onPurchased). That event is
  // best-effort — it can be missed (overlay closed early, sandbox flake), which
  // leaves the buyer staring at the same offer card even though the webhook
  // already granted the Kit. The grant lands on the PROJECT resource as
  // `pack.is_active`, refetched here via useProject (invalidated by the WS
  // `pack_purchase` reconcile / the post-checkout poll). When it flips true
  // while this paywall is open, the purchase definitively succeeded — show the
  // celebration regardless of whether the JS event arrived.
  useEffect(() => {
    if (isPackActive) {
      setDidPurchase(true);
    }
  }, [isPackActive]);

  // Purchase succeeded — stay open on a checkmark celebration instead of
  // closing, mirroring Apple's `activeCelebration` (and Android). The Kit grant
  // is owned by the webhook; this is the reward-moment UX. The Kit tag flips via
  // the onCompleted refresh/poll already kicked off in usePackCheckout.
  if (didPurchase) {
    return (
      <div className="flex flex-col gap-4 pb-4">
        <DrawerHeader className="items-center px-0 text-center">
          <DrawerTitle className="text-screen-subtitle text-center font-bold">
            {payload.product_name ?? productAttrs?.name ?? t('packActiveTitle')}
          </DrawerTitle>
          {buildingName && (
            <DrawerDescription className="text-card-meta text-muted-foreground text-center">
              {t('packActiveOn', { name: buildingName })}
            </DrawerDescription>
          )}
        </DrawerHeader>

        {/* Same white, borderless, soft-shadowed chrome as the offer card —
            the checkmark + outcome bullets read as the delivered Kit. */}
        <div className="flex w-full flex-col items-center gap-4 rounded-2xl bg-product-card p-4 text-center shadow-xl">
          <BadgeCheck className="text-primary h-10 w-10" aria-hidden="true" />
          {/* Bullets stay left-flagged even though the card centers the
              checkmark: the parent `text-center` would otherwise center the
              multi-line labels (icons left, ragged text), which reads worse
              than the clean left-aligned list of the offer card. */}
          <PaywallBullets bullets={payload.bullets} className="text-left" />
        </div>

        <DrawerFooter className="px-0">
          <Button className="w-full" onClick={onDismiss}>
            {t('proPlanDone')}
          </Button>
        </DrawerFooter>
      </div>
    );
  }

  // Offer card — clean white card lifted by a soft shadow (borderless, floating,
  // premium), mirroring the landing's white Facade Pack card; reads as a
  // prepared deliverable, not a cramped "budget" panel. Generous padding so it
  // breathes. Name + reference price head the card; the canonical outcome
  // bullets sit inside it. Without the catalog (still loading / failed) the
  // bullets render standalone so the server-driven value props never disappear
  // with the price. Built once and placed differently per layout (leads for
  // non-subscribers, below the "or" divider for Pro Plan holders).
  const offerCard = productAttrs ? (
    <div className="flex w-full flex-col gap-5 rounded-2xl bg-product-card p-4 shadow-xl">
      {/* Name + price head the card. `flex-wrap` so they sit side-by-side when
          they fit and the price WRAPS onto its own (full, multi-line) line when
          the name + a long currency ("10.000.000 COP") don't — the price must
          NEVER be hidden behind an ellipsis (kills conversion; App Store rejects
          a paywall where the price isn't visible). Mirrors Apple's
          `ViewThatFits` fallback to a stacked VStack. */}
      <div className="flex w-full flex-col gap-1">
        <div className="flex w-full flex-wrap items-start justify-between gap-x-4 gap-y-1">
          {/* Action-context name drops the storefront "Additional " modifier:
              prefer the payload's plain `product_name` over the catalog name. */}
          {/* Name + price share ONE larger type role (screen-subtitle, bold) so
              the offer reads as a confident hero — mirrors Apple, where both use
              `screenSubtitle`. The price must never ellipsize (App Store rejects
              a hidden price; long currencies wrap onto their own line via the
              parent `flex-wrap`). */}
          <h2 className="min-w-0 text-screen-subtitle text-foreground font-bold">
            {payload.product_name ?? productAttrs.name}
          </h2>
          {/* Server-computed, tax-inclusive total (same "Due today" number the
              Paddle checkout overlay shows) — falls back to the static USD
              reference price only when the country is unknown or the Paddle
              lookup failed. */}
          <p className="text-screen-subtitle text-foreground font-bold">
            {formatPackPrice(productAttrs)}
          </p>
        </div>
        {/* Billing-model promise ("Один объект · Разовая покупка · Без срока
            действия") — server `body`, placed directly under the name + price
            as the next line inside the card (parity with Android). */}
        {payload.body ? (
          <p className="text-card-meta text-muted-foreground">{payload.body}</p>
        ) : null}
      </div>
      <PaywallBullets bullets={payload.bullets} />
    </div>
  ) : (
    <PaywallBullets bullets={payload.bullets} />
  );

  return (
    <div className="flex flex-col gap-4 pb-4">
      {/* Back to the parent alert this body was swapped in from (only present
          when reached that way — see `onBack` doc comment above). Mirrors the
          corner-X close button's position on the opposite side. */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label={tCommon('back')}
          className="absolute left-4 top-4 rounded-full p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
      )}
      {/* Centered, slightly larger headline — mirrors the Apple/Android pack
          sheet (centered, bold, screenSubtitle). `text-balance` spreads long
          copy evenly instead of a ragged stack; the clamp shrinks a full
          sentence one step while short titles stay near the role size (Apple's
          lineLimit(3) + minimumScaleFactor(0.6), Android autoSize). */}
      <DrawerHeader className="items-center px-0 text-center">
        <DrawerTitle className="text-screen-subtitle text-balance text-center font-bold">
          {payload.title}
        </DrawerTitle>
        {/* Server-driven contextual note (e.g. FREE_DESIGNS_EXHAUSTED "come
            back tomorrow") — a small secondary line directly under the title,
            verbatim. Mirrors Apple/Android, which place it here (not in a banner
            box) so it reads as a calm subtitle, not an alert. */}
        {payload.note ? (
          <DrawerDescription className="text-card-meta text-muted-foreground text-center">
            {payload.note}
          </DrawerDescription>
        ) : null}
      </DrawerHeader>

      {/* The pack purchase IS the offer — the card leads and the checkout CTA
          is the single primary path. */}
      {offerCard}
      {/* `buildingVerbLabel` is set ONLY when the user has >1 building — it
          names the bound building so the multi-building owner knows which
          one this Kit attaches to. With a single building it's undefined and
          the block stays the plain store handoff (no extra verb capsule). */}
      <PackBuyCta
        className="mt-2"
        projectId={projectId}
        verbLabel={buildingVerbLabel}
        onLaunch={onCheckoutOpen}
        onPurchased={() => setDidPurchase(true)}
        onClosed={onCheckoutClose}
      />

      {/* Regional payment restriction (server-driven, e.g. RU/BY where Paddle
          cannot sell at all) — shown above the trust line so the buyer learns
          the restriction BEFORE tapping into a checkout that would dead-end. */}
      {payload.web_payment_notice ? (
        <NoticeBanner
          variant="warning"
          message={payload.web_payment_notice}
        />
      ) : null}

      {/* Trust microcopy — web checks out via Paddle, so it reads the paddle
          twin of the secure-payment line (Apple → App Store, Android → Google
          Play). Server-driven copy. */}
      {payload.secure_payment_paddle ? (
        <p className="text-card-meta text-muted-foreground flex items-center justify-center gap-1">
          <Lock className="h-3 w-3" aria-hidden="true" />
          {payload.secure_payment_paddle}
        </p>
      ) : null}

      {/* Terms footer — parity with the Pass paywall + Apple/Android sheet
          footers. Terms opens the in-app legal document in a nested dialog over
          the paywall. */}
      <div className="flex items-center justify-center gap-3 text-card-meta text-muted-foreground">
        <button
          type="button"
          onClick={() => setShowTerms(true)}
          className="transition-colors hover:text-foreground"
        >
          {tLegal('support.termsOfService')}
        </button>
      </div>

      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent
          showCloseButton
          className="max-h-[85vh] max-w-2xl overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>{tLegal('support.termsOfService')}</DialogTitle>
          </DialogHeader>
          <LegalDocumentPage type="terms" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
